import React, { useState } from "react";
import { Offcanvas, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { ApiHook } from "../../hooks/apiHook";
import { useQueryClient } from "@tanstack/react-query";
import SubmitButton from "../Common/buttons/SubmitButton";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import { reverseNumberDisplay } from "../../utils/currencyNumberDisplay";
import CurrencyInput from "react-currency-input-field";
import { EwalletService } from "../../services/ewallet/ewallet";

const EmoneyTransferOffcanvas = ({ balance, show, handleClose, currency }) => {
    const { t } = useTranslation();
    const [file, setFile] = useState(null);

    const [transferData, setTransferData] = useState({
        amount: "",
        transaction: "",
    });
    const transFee = useSelector(
        (state) => state.dashboard?.appLayout?.configuration?.transFee
    );
    const conversionFactor = useSelector(
        (state) => state?.user?.conversionFactor
    );
    const defaultCurrencyJson = useSelector(
        (state) => state?.user?.loginResponse?.defaultCurrency
    );

    const convertedTransFee = CurrencyConverter(transFee, conversionFactor)

    const defaultCurrency = defaultCurrencyJson
        ? JSON.parse(defaultCurrencyJson)
        : null;
    const queryClient = useQueryClient();
    const [errorMessage, setErrorMessage] = useState({
        amount: null,
        transaction: null,
    });

    const handleChange = (e) => {
        const { id, value } = e.target;
        setTransferData((prevData) => ({
            ...prevData,
            [id]: value,
        }));
        setErrorMessage((prevData) => ({
            ...prevData,
            [id]: null,
        }));
        if (value === null || value === "") {
            setErrorMessage((prev) => ({
                ...prev,
                [id]: "*Required",
            }));
        }
    };
    // const transferMutation = ApiHook.CallFundTransfer();
    const requestMutation = EwalletService.useRequestEmoney();
    const isFormValid = () => {
        return (
            transferData?.amount > 0 &&
            transferData?.transaction !== "" &&
            file !== null
        );
    };
    const handleSubmit = async (e) => {

        e.preventDefault();
        if (!isFormValid()) {
            return;
        }
        try {
            let convertAmount;
            if (currency.id === defaultCurrency.id) {
                convertAmount = reverseNumberDisplay(
                    CurrencyConverter(transferData?.amount, conversionFactor, 0)
                );
            } else {
                convertAmount = reverseNumberDisplay(
                    CurrencyConverter(transferData?.amount, conversionFactor, 1)
                );
            }
            const formData = new FormData();
            formData.append("file", file);
            formData.append("amount", convertAmount);
            formData.append("transaction", transferData?.transaction);
            await requestMutation.mutate(formData, {
                onSuccess: (res) => {
                    if (res.status) {
                        toast.success('Request Successfully Submitted')
                        setErrorMessage({
                            username: null,
                            transactionPassword: null,
                        });
                        setFile(null)
                        setTransferData({
                            username: "",
                            amount: "",
                            transactionPassword: "",
                        });
                        // queryClient.invalidateQueries({
                        //     queryKey: ["statement"],
                        // });
                        queryClient.invalidateQueries({
                            queryKey: ["get-request-emoney"],
                        });
                        handleClose(false);
                    } else {
                        toast.error(res?.message);
                    }
                },
            });
        } catch (error) {
            // Handle general error or network issue
            console.error("Error submitting transfer:", error);
        }
    };


    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        setFile(selectedFile);
    };

    return (
        <Offcanvas
            show={show}
            onHide={() => {
                handleClose();
                setErrorMessage({
                    amount: null,
                    transaction: null,
                });
                setTransferData({
                    amount: "",
                    transaction: "",
                });
                setFile(null);
            }}
            placement="end"
            id="ewalletTrnsfer"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Request Emoney</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t("amount")}
                            <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="amount-field input-group">
                            <Form.Control
                                as="select"
                                disabled
                                className="max-40"
                            >
                                <option value="">{currency?.symbolLeft}</option>
                            </Form.Control>
                            <CurrencyInput
                                name="amount"
                                id="amount"
                                placeholder={t("amount")}
                                value={transferData.amount}
                                onValueChange={(value, id) =>
                                    handleChange({ target: { value, id } })
                                }
                                required
                                allowNegativeValue={false}
                            // decimalsLimit={2}
                            />
                            <div className="number-field-invalid-feedback">
                                {errorMessage.amount}
                            </div>
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Transaction Note
                            <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="message-field input-group">
                            <Form.Control
                                as="textarea"
                                name="transaction"
                                id="transaction"
                                placeholder={'Transaction Note'}
                                value={transferData.transaction}
                                onChange={handleChange}
                                required
                                rows={4} // Adjust the number of rows as needed
                            />
                            <div className="number-field-invalid-feedback">
                                {errorMessage.message}
                            </div>
                        </div>
                    </Form.Group>


                    <Form.Group className="mb-3">
                        <Form.Label>
                            Select Receipt
                            <span className="text-danger">*</span>
                        </Form.Label>
                        <input
                            id="fileUpload"
                            type="file"
                            className="form-control"
                            name="fileUpload"
                            accept="image/png, image/jpeg, image/jpg"
                            onChange={handleFileChange}
                        />
                        <div className="text-danger mt-1">(Allowed types jpg|jpeg|png)</div>
                    </Form.Group>


                    <SubmitButton
                        isSubmitting={
                            !isFormValid() || requestMutation.isLoading
                        }
                        click={handleSubmit}
                        text="submit"
                        className={"btn btn-primary submit_btn"}
                        isLoading={requestMutation.isLoading}
                    />
                </Form>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default EmoneyTransferOffcanvas;